import React, { useRef, useState, useEffect } from "react";
import useImage from "use-image";
import axios from "axios";
import api, { API_BASE_URL } from "../../../../utils/api";
import Toolbar from "./Toolbar";
import CanvasStage from "./CanvasStage";

const TOOLBAR_WIDTH = 80;

const ImageAnnotatorCanvas = ({ file, requestId, onClose }) => {
  const [image] = useImage(`${API_BASE_URL}${file.file_url}`);
  const [imageProps, setImageProps] = useState({ x: 0, y: 0 });
  const [annotations, setAnnotations] = useState([]);
  const [newShape, setNewShape] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawMode, setDrawMode] = useState("rect"); // Start with rectangle tool

  const [labelPrompt, setLabelPrompt] = useState({ visible: false, x: 0, y: 0 });
  const [pendingShape, setPendingShape] = useState(null);
  const stageRef = useRef();

  useEffect(() => {
    if (image) {
      const centerX = window.innerWidth / 2 - image.width / 2 + TOOLBAR_WIDTH / 2;
      const centerY = (window.innerHeight - 56) / 2 - image.height / 2;
      setImageProps({ x: centerX, y: centerY });
    }
  }, [image]);

  useEffect(() => {
    const fetchAnnotations = async () => {
      try {
        const res = await api.get(`/annotations/${requestId}/${file.id}`);
        setAnnotations(res.data || []);
      } catch (err) {
        console.error("Failed to load annotations", err);
      }
    };
    fetchAnnotations();
  }, [file, requestId]);

  const handleUndo = () => {
    if (annotations.length > 0) {
      setAnnotations((prev) => prev.slice(0, -1));
    }
  };

  const handleSave = async () => {
    try {
      await api.post(`/annotations/save`, {
        request_id: requestId,
        file_id: file.id,
        data: annotations,
        created_by: 1,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save annotations", err);
    }
  };

  const handleLabelSubmit = (e) => {
    e.preventDefault();
    const label = e.target.label.value.trim();
    if (pendingShape) {
      const finalized = { ...pendingShape, label };
      setAnnotations([...annotations, finalized]);
    }
    setPendingShape(null);
    setLabelPrompt({ visible: false, x: 0, y: 0 });
  };

  const handleLabelCancel = () => {
    setPendingShape(null);
    setLabelPrompt({ visible: false, x: 0, y: 0 });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 2000, display: "flex" }}>
      <Toolbar setDrawMode={setDrawMode} handleUndo={handleUndo} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          backgroundColor: "#1976d2", color: "white", padding: "12px 20px",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <div>🖊️ Annotate Image: <strong>{file?.file_name}</strong></div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={handleSave}>💾 Save</button>
            <button onClick={onClose}>❌ Cancel</button>
          </div>
        </div>

        <CanvasStage
          image={image}
          imageProps={imageProps}
          stageRef={stageRef}
          annotations={annotations}
          setAnnotations={setAnnotations}
          newShape={newShape}
          setNewShape={setNewShape}
          isDrawing={isDrawing}
          setIsDrawing={setIsDrawing}
          drawMode={drawMode}
          setLabelPrompt={setLabelPrompt}
          setPendingShape={setPendingShape}
        />

        {labelPrompt.visible && (
          <div style={{
            position: "fixed",
            top: labelPrompt.y,
            left: labelPrompt.x + TOOLBAR_WIDTH,
            transform: "translate(-50%, -100%)",
            backgroundColor: "#fff",
            padding: 10,
            border: "1px solid #ccc",
            borderRadius: 4,
            zIndex: 2100,
          }}>
            <form onSubmit={handleLabelSubmit}>
              <input type="text" name="label" placeholder="Enter label" autoFocus />
              <button type="submit">OK</button>
              <button type="button" onClick={handleLabelCancel}>Cancel</button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageAnnotatorCanvas;
